import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refresh_token');

      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, {
            headers: { Authorization: `Bearer ${refreshToken}` },
          });
          const { access_token } = response.data;
          localStorage.setItem('access_token', access_token);
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }
    }
    return Promise.reject(error);
  }
);

// ─── Auth API ─────────────────────────────────────────────────────────────────
export const authAPI = {
  sendOtp: (email, recaptchaToken) => api.post('/auth/send-otp', {
    email,
    recaptcha_token: recaptchaToken,
  }),
  verifyOtp: (email, otp, firstName, middleName, lastName, recaptchaToken) =>
    api.post('/auth/verify-otp', {
      email,
      otp,
      first_name: firstName,
      middle_name: middleName,
      last_name: lastName,
      recaptcha_token: recaptchaToken,
    }),
  devLogin: (email, name) => api.post('/auth/dev-login', { email, name }),
  getMe: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh'),
  updateProfile: (data) => api.put('/auth/profile', data),
};

// ─── Contest API ──────────────────────────────────────────────────────────────
export const contestAPI = {
  list: (status) => (status ? api.get('/contests/', { params: { status } }) : api.get('/contests/')),
  get: (id) => api.get(`/contests/${id}`),
  register: (id) => api.post(`/contests/${id}/register`),
  unregister: (id) => api.post(`/contests/${id}/unregister`),
  getClarifications: (id) => api.get(`/contests/${id}/clarifications`),
  createClarification: (id, data) => api.post(`/contests/${id}/clarifications`, data),
};

// ─── Problem API ──────────────────────────────────────────────────────────────
export const problemAPI = {
  get: (id, params) => api.get(`/problems/${id}`, { params }),
  getByContest: (contestId) => api.get(`/problems/contest/${contestId}`),
  getSolution: (problemId) => api.get(`/problems/${problemId}/solution`),
  // Admin/Teacher solution sharing (via problem routes)
  shareSolution: (problemId, invCode, data) => api.post(`/problems/${problemId}/share-solution/${invCode}`, data),
  getSharedSolution: (problemId, invCode) => api.get(`/problems/${problemId}/share-solution/${invCode}`),
  deleteSharedSolution: (problemId, invCode) => api.delete(`/problems/${problemId}/share-solution/${invCode}`),
};

// ─── Analytics API ────────────────────────────────────────────────────────────
export const analyticsAPI = {
  save: (data) => api.post('/analytics/save', data),
  getMy: (problemId, contestId) => api.get(`/analytics/my/${problemId}/${contestId}`),
  getContest: (contestId) => api.get(`/analytics/contest/${contestId}`),
  getUserContest: (userId, contestId) => api.get(`/analytics/user/${userId}/contest/${contestId}`),
  getLiveCode: (contestId, problemId, invCode) =>
    api.get(`/analytics/live-code/${contestId}/${problemId}`, { params: invCode ? { code: invCode } : {} }),
};

// ─── Submission API ───────────────────────────────────────────────────────────
export const submissionAPI = {
  create: (data) => api.post('/submissions/', data),
  get: (id) => api.get(`/submissions/${id}`),
  getMy: (params) => api.get('/submissions/my', { params }),
  runSample: (data) => api.post('/submissions/run-sample', data),
  runCustom: (data) => api.post('/submissions/run-custom', data),
  checkStatus: (id) => api.get(`/submissions/status/${id}`),
};

// ─── Standing API ─────────────────────────────────────────────────────────────
export const standingAPI = {
  get: (contestId, params) => api.get(`/standings/${contestId}`, { params }),
  filters: (contestId) => api.get(`/standings/${contestId}/filters`),
  hideUser: (contestId, userId) => api.put(`/standings/${contestId}/hide-user`, { user_id: userId }),
  showUser: (contestId, userId) => api.put(`/standings/${contestId}/show-user`, { user_id: userId }),
};

// ─── System API ───────────────────────────────────────────────────────────────
export const systemAPI = {
  getBanner: () => api.get('/system/banner'),
};

// ─── Admin API ────────────────────────────────────────────────────────────────
export const adminAPI = {
  // Contests
  listContests: () => api.get('/admin/contests/list'),
  createContest: (data) => api.post('/admin/contests', data),
  generateTemplateContest: (data) => api.post('/admin/contests/generate-template', data),
  generateAIContest: (data) => api.post('/admin/contests/generate-ai', data),
  updateContest: (id, data) => api.put(`/admin/contests/${id}`, data),
  deleteContest: (id) => api.delete(`/admin/contests/${id}`),

  // Problems
  createProblem: (data) => api.post('/admin/problems', data),
  updateProblem: (id, data) => api.put(`/admin/problems/${id}`, data),
  deleteProblem: (id) => api.delete(`/admin/problems/${id}`),

  // Test cases
  createTestCase: (data) => api.post('/admin/test-cases', data),
  updateTestCase: (id, data) => api.put(`/admin/test-cases/${id}`, data),
  deleteTestCase: (id) => api.delete(`/admin/test-cases/${id}`),
  bulkCreateTestCases: (data) => api.post('/admin/test-cases/bulk', data),
  syncTestCases: (problemId, data) => api.put(`/admin/problems/${problemId}/test-cases/sync`, data),

  // Submissions
  listSubmissions: (params) => api.get('/admin/submissions', { params }),
  getSubmission: (id) => api.get(`/admin/submissions/${id}`),

  // Announcements
  createAnnouncement: (data) => api.post('/admin/announcements', data),

  // Clarifications
  answerClarification: (id, data) => api.put(`/admin/clarifications/${id}/answer`, data),

  // AI
  generateProblem: (data) => api.post('/admin/ai/generate-problem', data),
  generateProblems: (data) => api.post('/admin/ai/generate-problems', data),

  // Banner
  getBanner: () => api.get('/admin/banner'),
  upsertBanner: (data) => api.post('/admin/banner', data),
  clearBanner: () => api.delete('/admin/banner'),

  // Export
  exportStandings: (contestId) =>
    api.get(`/admin/export/standings/${contestId}`, { responseType: 'blob' }),

  // Users
  listUsers: () => api.get('/admin/users'),
  updateUserRole: (id, role) => api.put(`/admin/users/${id}/role`, { role }),
  toggleActivation: (id) => api.put(`/admin/users/${id}/toggle-activate`),
};

// ─── Invitation API ───────────────────────────────────────────────────────────
export const invitationAPI = {
  // Student
  activate: (code) => api.post('/invitation/activate', { code }),
  selectCode: (code) => api.post('/invitation/select', { code }),
  myCodes: () => api.get('/invitation/my-codes'),
  status: () => api.get('/invitation/status'),

  // Admin - codes CRUD
  listCodes: () => api.get('/invitation/codes'),
  createCode: (data) => api.post('/invitation/codes', data),
  updateCode: (id, data) => api.put(`/invitation/codes/${id}`, data),
  deleteCode: (id) => api.delete(`/invitation/codes/${id}`),
  assignContests: (id, contestIds) => api.put(`/invitation/codes/${id}/contests`, { contest_ids: contestIds }),

  // Admin - group stats
  codeUsers: (id) => api.get(`/invitation/codes/${id}/users`),
  removeCodeUser: (codeId, userId) => api.delete(`/invitation/codes/${codeId}/users/${userId}`),
  unblockCodeUserLogin: (codeId, userId) => api.post(`/invitation/codes/${codeId}/users/${userId}/unblock-login`),
  blockCodeUser: (codeId, userId) => api.post(`/invitation/codes/${codeId}/users/${userId}/block`),
  unblockCodeUser: (codeId, userId) => api.post(`/invitation/codes/${codeId}/users/${userId}/unblock-code`),
  codeStats: (id) => api.get(`/invitation/codes/${id}/stats`),
  userPerformance: (codeId, userId) =>
    api.get(`/invitation/codes/${codeId}/user/${userId}/performance`),
  contestLeaderboard: (codeId, contestId) =>
    api.get(`/invitation/codes/${codeId}/contest/${contestId}/leaderboard`),
};

// ─── Report API ───────────────────────────────────────────────────────────────
export const reportAPI = {
  preview: (contestId, userId) => api.get(`/reports/preview/${contestId}/${userId}`),
  send: (contestId, userId) => api.post(`/reports/send/${contestId}/${userId}`),
  sendAll: (contestId) => api.post(`/reports/send-all/${contestId}`),
};

// ─── Teacher API ──────────────────────────────────────────────────────────────
export const teacherAPI = {
  listContests: () => api.get('/teacher/contests'),
  createContest: (data) => api.post('/teacher/contests', data),
  updateContest: (id, data) => api.put(`/teacher/contests/${id}`, data),
  deleteContest: (id) => api.delete(`/teacher/contests/${id}`),

  createProblem: (data) => api.post('/teacher/problems', data),
  updateProblem: (id, data) => api.put(`/teacher/problems/${id}`, data),
  deleteProblem: (id) => api.delete(`/teacher/problems/${id}`),

  listCodes: () => api.get('/teacher/invitation/codes'),
  codeUsers: (id) => api.get(`/teacher/invitation/codes/${id}/users`),
  codeStats: (id) => api.get(`/teacher/invitation/codes/${id}/stats`),
  userPerformance: (codeId, userId) => api.get(`/teacher/invitation/codes/${codeId}/user/${userId}/performance`),
  contestLeaderboard: (codeId, contestId) => api.get(`/teacher/invitation/codes/${codeId}/contest/${contestId}/leaderboard`),

  // Solutions
  shareSolution: (problemId, invitationCode, data) => api.post(`/teacher/solutions/${problemId}/${invitationCode}`, data),
  getSolution: (problemId, invitationCode) => api.get(`/teacher/solutions/${problemId}/${invitationCode}`),
  deleteSolution: (problemId, invitationCode) => api.delete(`/teacher/solutions/${problemId}/${invitationCode}`),
};

// ─── Whiteboard API ─────────────────────────────────────────────────────
export const whiteboardAPI = {
  // groupCode scopes the board to a specific invitation-code group so that
  // admin/teacher actions only affect their chosen group of students.
  get: (contestId, problemId, version = -1, groupCode = '') =>
    api.get(`/whiteboard/${contestId}/${problemId}`, { params: { v: version, group: groupCode } }),
  addStroke: (contestId, problemId, stroke, groupCode = '') =>
    api.post(`/whiteboard/${contestId}/${problemId}/stroke`, { ...stroke, group: groupCode }),
  deleteStroke: (contestId, problemId, strokeId, groupCode = '') =>
    api.delete(`/whiteboard/${contestId}/${problemId}/stroke/${strokeId}`, { params: { group: groupCode } }),
  clear: (contestId, problemId, groupCode = '') =>
    api.delete(`/whiteboard/${contestId}/${problemId}/clear`, { params: { group: groupCode } }),
  setLock: (contestId, problemId, locked, groupCode = '') =>
    api.put(`/whiteboard/${contestId}/${problemId}/lock`, { locked, group: groupCode }),
};

export default api;
